import { AppState } from '../../types';
import {
  CustomField,
  parentHideKey,
  isFieldHidden,
  unscopedCustomFieldIdent,
} from '@fullstory/search-vars';
import { createSelector } from 'reselect';
import { getDefinedEventsById, getAllDefinedEvents } from './definedEvents/selectors';
import { getNamedElementsById, getAllNamedElements } from '../namedElements/selectors';
import { SearchContext, SearchCacheKey, ApiEventDetails } from './types';
import { createDeepEqualSelector } from '@fullstory/redux-util/utils/selectors';
import { hashObject } from '@fullstory/util/hash';
import { int64 } from '@fullstory/proto/google/protobuf';
import { pagesById } from '../pages/selectors';
import { getWatchedNamedElementIds } from '../watchedElements/selectors';
import { CustomSchemaCounts } from '@fullstory/proto/fs/services/fieldmeta';
import {
  allMappedSpecObjects,
  allSpecObjectsBySlug,
  getTopUnmappedSpecObjects,
  TargetType,
} from '../specs';
import { pickBy } from 'lodash';

export const getUserFields = (state: AppState): CustomField[] =>
  state.searchMeta.meta.userFields ?? [];
export const getFsEvtFields = (state: AppState): CustomField[] =>
  state.searchMeta.meta.fsEvtFields ?? [];
export const getPagePropsLoading = (state: AppState): Set<number> =>
  state.searchMeta.meta.pageFields?.loading || new Set();
export const getPagePropsLastLoaded = (state: AppState): Map<number, Date> =>
  state.searchMeta.meta.pageFields?.lastLoaded || new Map();
export const getPageProps = (state: AppState): Map<number, CustomField[]> =>
  state.searchMeta.meta.pageFields?.values ?? new Map();
export const getElementPropsLoading = (state: AppState): Set<string> =>
  state.searchMeta.meta.elementFields?.loading || new Set();
export const getElementPropsLastLoaded = (state: AppState): Map<string, Date> =>
  state.searchMeta.meta.elementFields?.lastLoaded || new Map();
export const getElementProps = (state: AppState): Map<string, CustomField[]> =>
  state.searchMeta.meta.elementFields?.values ?? new Map();
export const getAllCustomEventNames = (state: AppState): string[] =>
  state.searchMeta.meta.customEventNames || [];
export const getHiddenVars = (state: AppState): Set<string> =>
  state.searchMeta.meta.hiddenVars ?? new Set();
export const getCustomEventPropsLoading = (state: AppState): Set<string> =>
  state.searchMeta.meta.customEventMeta?.loading || new Set();
export const getCustomEventPropsLastLoaded = (state: AppState): Map<string, Date> =>
  state.searchMeta.meta.customEventMeta?.lastLoaded || new Map();
export const getCustomEventProps = (state: AppState): Map<string, CustomField[]> =>
  state.searchMeta.meta.customEventMeta?.values || new Map();
export const getEventConnectionsLoading = (state: AppState): Set<string> =>
  state.searchMeta.meta.eventConnectionsByField?.loading ?? new Set();
export const getEventConnectionsLastLoaded = (state: AppState): Map<string, Date> =>
  state.searchMeta.meta.eventConnectionsByField?.lastLoaded ?? new Map();
export const getPageConnectionsLoading = (state: AppState): Set<string> =>
  state.searchMeta.meta.pageConnectionsByField?.loading ?? new Set();
export const getPageConnectionsLastLoaded = (state: AppState): Map<string, Date> =>
  state.searchMeta.meta.pageConnectionsByField?.lastLoaded ?? new Map();
export const getElementConnectionsLoading = (state: AppState): Set<string> =>
  state.searchMeta.meta.elementConnectionsByField?.loading ?? new Set();
export const getElementConnectionsLastLoaded = (state: AppState): Map<string, Date> =>
  state.searchMeta.meta.elementConnectionsByField?.lastLoaded ?? new Map();
export const getEventConnectionsByField = (state: AppState): Map<string, Set<string>> =>
  state.searchMeta.meta.eventConnectionsByField?.values ?? new Map();
export const getPageConnectionsByField = (state: AppState): Map<string, Set<number>> =>
  state.searchMeta.meta.pageConnectionsByField?.values ?? new Map();
export const getElementConnectionsByField = (state: AppState): Map<string, Set<string>> =>
  state.searchMeta.meta.elementConnectionsByField?.values ?? new Map();

export const getCustomSchemaCounts = (state: AppState): CustomSchemaCounts =>
  state.searchMeta.meta.counts;
export const getCustomSchemaLimits = (state: AppState): CustomSchemaCounts =>
  state.searchMeta.meta.limits;

export const getAllCustomEventsReady = (state: AppState) => state.searchMeta.meta.loaded;

export const getCustomEventsWithNoProps = createSelector(getCustomEventProps, props => {
  const noProps = new Set<string>();
  props.forEach((fields, name) => fields.length === 0 && noProps.add(name));
  return noProps;
});

export const getVisibleCustomEventsNames = createSelector(
  getAllCustomEventNames,
  getHiddenVars,
  (names, hidden) => names.filter(name => !hidden.has(parentHideKey(name))),
);
const _getHiddenCustomEventsNames = createSelector(
  getAllCustomEventNames,
  getHiddenVars,
  (names, hidden) => names.filter(name => hidden.has(parentHideKey(name))),
);
export const getHiddenCustomEventsNames = createDeepEqualSelector(
  _getHiddenCustomEventsNames,
  value => value,
);

export const getVisibleCustomEventProps = createSelector(
  getCustomEventProps,
  getHiddenVars,
  (props, hidden) => {
    const visible = new Map<string, CustomField[]>();
    props.forEach((fields, eventName) =>
      visible.set(
        eventName,
        fields.filter(f => !isFieldHidden(f, eventName, hidden)),
      ),
    );
    return visible;
  },
);
const _getHiddenCustomEventProps = createSelector(
  getCustomEventProps,
  getHiddenVars,
  (props, hidden) => {
    const hiddenFields: CustomField[] = [];
    const handled = new Set<string>();
    props.forEach((fields, eventName) =>
      fields.forEach(f => {
        const typedIdent = unscopedCustomFieldIdent(f);
        if (isFieldHidden(f, eventName, hidden) && !handled.has(typedIdent)) {
          hiddenFields.push(f);
          handled.add(typedIdent);
        }
      }),
    );
    return hiddenFields;
  },
);
export const getHiddenCustomEventProps = createDeepEqualSelector(
  _getHiddenCustomEventProps,
  value => value,
);

export const getVisibleUserFields = createSelector(getUserFields, getHiddenVars, (fields, hidden) =>
  fields.filter(f => !isFieldHidden(f, '', hidden)),
);
const _getHiddenUserFields = createSelector(getUserFields, getHiddenVars, (fields, hidden) =>
  fields.filter(f => isFieldHidden(f, '', hidden)),
);
const getHiddenUserFields = createDeepEqualSelector(_getHiddenUserFields, value => value);

export const getVisibleFsEvtFields = createSelector(
  getFsEvtFields,
  getHiddenVars,
  (fields, hidden) => fields.filter(f => !isFieldHidden(f, '', hidden)),
);
const _getHiddenFsEvtFields = createSelector(getFsEvtFields, getHiddenVars, (fields, hidden) =>
  fields.filter(f => isFieldHidden(f, '', hidden)),
);
const getHiddenFsEvtFields = createDeepEqualSelector(_getHiddenFsEvtFields, value => value);

export const getPageFields = createSelector(getPageProps, props => props.get(0) ?? []);
export const getVisiblePageProps = createSelector(getPageProps, getHiddenVars, (props, hidden) => {
  const visible = new Map<number, CustomField[]>();
  props.forEach((fields, pageDefId) =>
    visible.set(
      pageDefId,
      fields.filter(f => !isFieldHidden(f, pageDefId, hidden)),
    ),
  );
  return visible;
});
export const getVisiblePageFields = createSelector(
  getVisiblePageProps,
  props => props.get(0) ?? [],
);
const _getHiddenPageProps = createSelector(getPageProps, getHiddenVars, (props, hidden) => {
  const hiddenFields: CustomField[] = [];
  const handled = new Set<string>();
  props.forEach((fields, pageDefId) =>
    fields.forEach(f => {
      const typedIdent = unscopedCustomFieldIdent(f);
      if (isFieldHidden(f, pageDefId, hidden) && !handled.has(typedIdent)) {
        hiddenFields.push(f);
        handled.add(typedIdent);
      }
    }),
  );
  return hiddenFields;
});
// returns any page props that are hidden in at least one situation
// if you need to know exact connections for visible status, use getVisiblePageProps first
export const getHiddenPageProps = createDeepEqualSelector(_getHiddenPageProps, value => value);

export const getElementFields = createSelector(getElementProps, props => props.get('') ?? []);
export const getVisibleElementProps = createSelector(
  getElementProps,
  getHiddenVars,
  (props, hidden) => {
    const visible = new Map<string, CustomField[]>();
    props.forEach((fields, namedElementId) =>
      visible.set(
        namedElementId,
        fields.filter(f => !isFieldHidden(f, namedElementId, hidden)),
      ),
    );
    return visible;
  },
);
const _getHiddenElementProps = createSelector(getElementProps, getHiddenVars, (props, hidden) => {
  const hiddenFields: CustomField[] = [];
  const handled = new Set<string>();
  props.forEach((fields, namedElementId) =>
    fields.forEach(f => {
      const typedIdent = unscopedCustomFieldIdent(f);
      if (isFieldHidden(f, namedElementId, hidden) && !handled.has(typedIdent)) {
        hiddenFields.push(f);
        handled.add(typedIdent);
      }
    }),
  );
  return hiddenFields;
});
// returns any element props that are hidden in at least one situation
// if you need to know exact connections for visible status, use getVisibleElementProps first
export const getHiddenElementProps = createDeepEqualSelector(
  _getHiddenElementProps,
  value => value,
);

const getCustomApiEvents = createSelector(
  getAllCustomEventNames,
  getHiddenVars,
  (customEventNames, hiddenVars) => {
    const events: ApiEventDetails[] = customEventNames.map(name => ({
      name,
      archived: hiddenVars.has(parentHideKey(name)),
    }));
    return events;
  },
);

export const getCustomApiEventsMap = createSelector(getCustomApiEvents, apiEvents => {
  const eventsMap = new Map<string, ApiEventDetails>();
  apiEvents.forEach(apiEvent => eventsMap.set(apiEvent.name, apiEvent));
  return eventsMap;
});

const hashForSearchCacheKey = (idsAndVersion: { id: string; version: number }[]): number => {
  idsAndVersion.sort((a, b) => a.id.localeCompare(b.id));
  return hashObject(idsAndVersion);
};

const _getSearchCacheKey = createSelector(
  getAllDefinedEvents,
  getAllNamedElements,
  allMappedSpecObjects,
  (definedEvents, namedElements, mappedObjects): SearchCacheKey => ({
    definedEventVersionsHash: hashForSearchCacheKey(
      definedEvents.map(de => ({
        id: de.definedEvent!.id,
        version: int64.toNumber(de.metadata?.version || null),
      })),
    ),
    namedElementVersionsHash: hashForSearchCacheKey(
      namedElements.map(ne => ({
        id: ne.namedElement!.id,
        version: int64.toNumber(ne.metadata?.version || null),
      })),
    ),
    specObjectVersionsHash: hashForSearchCacheKey(
      mappedObjects.map(object => ({
        id: object.mapping.objectSlug,
        version: int64.toNumber(object.mapping.metadata.version),
      })),
    ),
  }),
);

// Provides back a struct that can be used to bust caches that rely on search objects.
// This will change with *any* search object save / edit and perform wide sweeping cache busting,
// but APIs that care will still use cached results on the server if referenced objects for a given API call
// did not change. We are erring on the side of the client making more requests on search model updates to ensure
// correct visualization data.
export const getSearchCacheKey = createDeepEqualSelector(
  _getSearchCacheKey,
  (searchCacheKey: SearchCacheKey) => searchCacheKey,
);

const getSearchContextCustomEvents = createDeepEqualSelector(
  getVisibleCustomEventsNames,
  getHiddenCustomEventsNames,
  (visible, hidden) => ({
    visible,
    hidden,
  }),
);

const getSearchContextCustomEventProps = createDeepEqualSelector(
  getVisibleCustomEventProps,
  getHiddenCustomEventProps,
  (visible, hidden) => ({
    visible,
    hidden,
  }),
);

const getSearchContextUserFields = createDeepEqualSelector(
  getVisibleUserFields,
  getHiddenUserFields,
  (visible, hidden) => ({
    visible,
    hidden,
  }),
);

const getSearchContextFsevtFields = createDeepEqualSelector(
  getVisibleFsEvtFields,
  getHiddenFsEvtFields,
  (visible, hidden) => ({
    visible,
    hidden,
  }),
);

const getSearchContextPageProps = createDeepEqualSelector(
  getVisiblePageProps,
  getHiddenPageProps,
  (visible, hidden) => ({
    visible,
    hidden,
  }),
);

const getSearchContextElementProps = createDeepEqualSelector(
  getVisibleElementProps,
  getHiddenElementProps,
  (visible, hidden) => ({
    visible,
    hidden,
  }),
);

const getSearchContextDefinedEvents = createDeepEqualSelector(
  getDefinedEventsById,
  definedEvents => definedEvents,
);

const getSearchContextNamedElements = createDeepEqualSelector(
  getNamedElementsById,
  namedElements => namedElements,
);

const getSearchContextWatchedNamedElementIds = createDeepEqualSelector(
  getWatchedNamedElementIds,
  watchedNamedElementIds => watchedNamedElementIds,
);

const getSearchContextPagesById = createDeepEqualSelector(pagesById, pagesById => pagesById);

const getSearchContextSpecObjects = createDeepEqualSelector(allSpecObjectsBySlug, specObjects => ({
  value: pickBy(specObjects, object => object.active && object.mapping),

  // Remove any mapping for a spec object that _could_ be mapped to a defined
  // event, regardless of whether it's _currently_ mapped to a defined event.
  valueWithoutDefinedEvents: pickBy(
    specObjects,
    object =>
      object.active && object.mapping && !object.mappableTypes.includes(TargetType.DefinedEvent),
  ),
}));

const getSearchContextSpecTopUnmapped = createDeepEqualSelector(
  getTopUnmappedSpecObjects,
  specTopUnmapped => ({
    value: specTopUnmapped,
    valueWithoutEvents: specTopUnmapped.filter(
      object =>
        !object.mappableTypes.some(t => t === TargetType.ApiEvent || t === TargetType.DefinedEvent),
    ),
  }),
);

export const getSearchContext = createSelector(
  getSearchContextCustomEvents,
  getSearchContextCustomEventProps,
  getSearchContextUserFields,
  getSearchContextPageProps,
  getSearchContextElementProps,
  getSearchContextDefinedEvents,
  getSearchContextNamedElements,
  getSearchContextWatchedNamedElementIds,
  getSearchContextPagesById,
  getSearchContextSpecObjects,
  getSearchContextSpecTopUnmapped,
  getSearchContextFsevtFields,
  (
    { visible: customEventNames, hidden: hiddenCustomEventNames },
    { visible: customEventProps, hidden: hiddenCustomEventProps },
    { visible: userVars, hidden: hiddenUserVars },
    { visible: pageProps, hidden: hiddenPageProps },
    { visible: elementProps, hidden: hiddenElementProps },
    definedEvents,
    namedElements,
    watchedNamedElementIds,
    pageDefinitions,
    specObjects,
    specTopUnmapped,
    { visible: fsEvtVars, hidden: hiddenFsEvtVars },
  ): SearchContext => ({
    customEventNames,
    hiddenCustomEventNames,
    customEventProps,
    hiddenCustomEventProps,
    definedEvents,
    namedElements,
    watchedNamedElementIds,
    userVars,
    hiddenUserVars,
    pageProps,
    hiddenPageProps,
    pageDefinitions,
    elementProps,
    hiddenElementProps,
    specObjects: specObjects.value,
    specTopUnmapped: specTopUnmapped.value,
    fsevtVars: fsEvtVars,
    hiddenFsevtVars: hiddenFsEvtVars,
  }),
);

const emptyDefinedEvents = new Map();

export const getSearchContextWithoutDefinedEvents = createSelector(
  getSearchContext,
  getSearchContextSpecObjects,
  getSearchContextSpecTopUnmapped,
  (searchContext, specObjects, specTopUnmapped): SearchContext => ({
    ...searchContext,
    // Remove all defined events from the context.
    definedEvents: emptyDefinedEvents,
    specObjects: specObjects.valueWithoutDefinedEvents,
    specTopUnmapped: specTopUnmapped.valueWithoutEvents,
  }),
);
